/**
 * Assistant Visible Content
 *
 * Removes internal prompt/tool artifacts before assistant text is displayed or
 * persisted.
 *
 * Primary responsibilities:
 * - Strip stale prior-tool prompt blocks from assistant-visible content
 * - Keep chat rendering and persistence on the same sanitization path
 *
 * Dependencies:
 * - None
 */

const assistantArtifactMarkers = [
  '[Prior tool results retained for this conversation]',
  '[Internal retained tool context]',
  'Internal retained tool context for continuity only.'
]

const internalToolLinePattern =
  /^\s*(?:set[_\s-]*todos|update[_\s-]*todo[_\s-]*status|tool[_\s-]*result|tool results retained)\b[:({\s-]/i

export function sanitizeAssistantVisibleContent(content: string) {
  const markerIndex = assistantArtifactMarkers
    .map((marker) => content.indexOf(marker))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0]

  const trimmedContent = markerIndex == null ? content : content.slice(0, markerIndex)

  const withoutInternalLines = trimmedContent
    .split('\n')
    .filter((line) => !internalToolLinePattern.test(line))
    .join('\n')
    .trimEnd()

  // When the model responds, runs a tool, then continues, the continuation
  // often begins with a markdown heading (e.g. "## Title") glued directly onto
  // the previous sentence ("…context.## Title"). Because ATX headings only
  // parse at the start of a line, the glued form renders inline instead of as a
  // heading and reads as one run-on line. Insert a blank line before an ATX
  // heading marker that is stuck to preceding non-newline text. Require a space
  // after the #'s and a preceding non-space/non-# char so inline uses like "C#"
  // or "#tag" are left untouched.
  return withoutInternalLines.replace(
    /([^\n#\s])(#{1,6}\s+)/g,
    '$1\n\n$2'
  )
}
